import React, { useMemo } from 'react';
import { Spinner } from "reactstrap";
import { useParams } from "react-router-dom";

const SEV = {
  Critical: { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
  High:     { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" },
  Medium:   { bg: "#fffbeb", color: "#d97706", border: "#fde68a" },
  Low:      { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
};

const SevBadge = ({ value }) => {
  const s = SEV[value] || { bg: "#f3f4f6", color: "#6b7280", border: "#e5e7eb" };
  return (
    <span style={{
      display: "inline-block", fontSize: 11, fontWeight: 700,
      padding: "2px 10px", borderRadius: 20,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>
      {value || "N/A"}
    </span>
  );
};

function AssetOverview({ reportData, loading }) {
  const { agent } = useParams();

  const assets = useMemo(() => {
    if (!reportData) return [];
    const severityOrder = { Critical: 1, High: 2, Medium: 3, Low: 4 };

    if (agent?.toLowerCase() === "all") {
      return (reportData.assetOverview || []).sort((a, b) => {
        const sevA = severityOrder[a.asset_criticality] || 5;
        const sevB = severityOrder[b.asset_criticality] || 5;
        if (sevA !== sevB) return sevA - sevB;
        return new Date(b.timestamp || b.created_at || 0) - new Date(a.timestamp || a.created_at || 0);
      });
    }

    const sourceRows = (reportData.compliance?.length > 0)
      ? reportData.compliance
      : reportData.threats || [];

    return sourceRows
      .filter(a => ["Critical", "High", "Medium", "Low"].includes(a.asset_criticality))
      .sort((a, b) => {
        const sevA = severityOrder[a.asset_criticality] || 5;
        const sevB = severityOrder[b.asset_criticality] || 5;
        if (sevA !== sevB) return sevA - sevB;
        return new Date(b.timestamp || b.created_at || 0) - new Date(a.timestamp || a.created_at || 0);
      })
      .slice(0, 5);
  }, [reportData, agent]);

  return (
    <div style={{ marginBottom: 28 }}>
      {/* Section header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div style={{ width: 4, height: 20, background: "#FF5B00", borderRadius: 2, flexShrink: 0 }} />
        <h6 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#1f2937", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {agent?.toLowerCase() === "all" ? "Asset Overview — All Agents" : `Asset Overview — ${agent}`}
        </h6>
      </div>

      <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem", color: "#9ca3af", fontSize: 13 }}>
            <Spinner size="sm" style={{ marginRight: 8, color: "#FF5B00" }} />
            Loading asset data...
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                {["Agent Name", "Scan Date", "Threat Description", "Severity"].map(h => (
                  <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #e5e7eb" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assets.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ padding: "2rem", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                    No assets found for {agent}.
                  </td>
                </tr>
              ) : (
                assets.map((a, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f3f4f6", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                    <td style={{ padding: "10px 16px", fontWeight: 600, color: "#1f2937" }}>{a.agent_name || agent}</td>
                    <td style={{ padding: "10px 16px", color: "#6b7280" }}>
                      {a.created_at ? new Date(a.created_at).toLocaleDateString()
                        : a.timestamp ? new Date(a.timestamp).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td style={{ padding: "10px 16px", color: "#374151", maxWidth: 320 }}>
                      <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {a.threat_description || a.threat_type || "N/A"}
                      </span>
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      <SevBadge value={a.asset_criticality} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AssetOverview;
